import React from 'react';
import PromptCard from '../PromptCard';

interface BookmateGuideProps {
  onPromptClick: (prompt: string) => void;
}

const BookmateGuide: React.FC<BookmateGuideProps> = ({ onPromptClick }) => {
  const conceptPrompts = [
    "Create a tutorial on object-oriented programming in Java",
    "Teach me Python list comprehensions with examples",
    "Explain binary search algorithm step by step in C++",
    "Create a tutorial on error handling in Rust",
    "Teach me how goroutines and channels work in Go",
    "Explain pointers and memory management in C"
  ];

  const dualLingoPrompts = [
    "Create a dual-lingo tutorial comparing Python and Java loops",
    "Compare class syntax between Java and C++",
    "Create a dual-lingo guide on error handling: Python vs Rust",
    "Show me how sorting works in both Go and Python"
  ];

  const projectPrompts = [
    "Create a tutorial: Build a simple calculator in Python",
    "Teach me how to create a student grade tracker in Java",
    "Build a linked list implementation tutorial in C++",
    "Create a tutorial on building a CLI tool in Go"
  ];

  const deepDivePrompts = [
    "Create a comprehensive tutorial on Java exception handling",
    "Teach me everything about Python dictionaries",
    "Explain data structures: stacks and queues in C++",
    "Create a complete guide to concurrency in Go"
  ];

  return (
    <div className="documentation-content">
      <h1>Bookmate - Tutorial Generator</h1>
      <p className="intro-text">
        Bookmate creates interactive tutorials you can read, share, and download as PDFs.
        Click any prompt to generate a tutorial!
      </p>

      <div className="section">
        <h2>Concept Tutorials</h2>
        <p className="section-desc">Learn fundamental programming concepts</p>
        {conceptPrompts.map((prompt, index) => (
          <PromptCard key={index} prompt={prompt} onClick={onPromptClick} />
        ))}
      </div>

      <div className="section">
        <h2>Dual-Language Learning</h2>
        <p className="section-desc">Compare two languages side-by-side</p>
        {dualLingoPrompts.map((prompt, index) => (
          <PromptCard key={index} prompt={prompt} onClick={onPromptClick} />
        ))}
      </div>

      <div className="section">
        <h2>Project-Based Learning</h2>
        <p className="section-desc">Build projects step-by-step</p>
        {projectPrompts.map((prompt, index) => (
          <PromptCard key={index} prompt={prompt} onClick={onPromptClick} />
        ))}
      </div>

      <div className="section">
        <h2>Topic Deep-Dives</h2>
        <p className="section-desc">Master complex topics thoroughly</p>
        {deepDivePrompts.map((prompt, index) => (
          <PromptCard key={index} prompt={prompt} onClick={onPromptClick} />
        ))}
      </div>

      <div className="tips-section">
        <h3>Bookmate Features</h3>
        <ul>
          <li><strong>Read Anywhere</strong>: Tutorials saved to your library</li>
          <li><strong>Run Code</strong>: Execute code examples instantly</li>
          <li><strong>Download PDF</strong>: Take tutorials offline</li>
          <li><strong>Share Links</strong>: Share tutorials with classmates</li>
          <li><strong>Dual-Lingo</strong>: Compare two languages side-by-side</li>
        </ul>
      </div>

      <div className="tips-section">
        <h3>Tips for Better Tutorials</h3>
        <ul>
          <li>Be specific about the topic you want to learn</li>
          <li>Mention your skill level (beginner, intermediate, advanced)</li>
          <li>Ask for examples and practice exercises</li>
          <li>Request dual-lingo for language comparison</li>
        </ul>
      </div>
    </div>
  );
};

export default BookmateGuide;
